import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MessageSquare } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useLiveResource } from "@/hooks/useLiveResource";
import { useChatWorkspace } from "./useChatWorkspace";

const ChatWorkspaceContext = createContext(null);
export const useChat = () => useContext(ChatWorkspaceContext);
// The dashboard shell needs only the agent's name and the full-screen flag. The chat context changes on every keystroke
// (the draft lives in the controller), and re-rendering the shell per keystroke re-ran Radix Popper's anchor effect in
// its two menus; React 19 counted the update that effect leaves pending as a nested update, so a fast burst of typing
// hit "Maximum update depth exceeded" about every 51 keys and lost that keystroke. This value only changes with them.
const ChatShellContext = createContext(null);
export const useChatShell = () => useContext(ChatShellContext);

function ScopedWorkspace({ children, scopeKey }) {
  const { api, activeClient, readOnly = false, readOnlyMessage = "" } = useApp();
  const profile = useLiveResource("getAgentProfile", [], { refreshMs: 60_000 });
  const controller = useChatWorkspace({ api, scopeKey, agentName: profile.data?.agent?.name });
  const { refreshThinking } = controller;
  const { pathname } = useLocation();
  const previousPath = useRef(pathname);
  useEffect(() => {
    if (pathname === "/agent/chat" && previousPath.current !== pathname) refreshThinking();
    previousPath.current = pathname;
  }, [pathname, refreshThinking]);
  const name = controller.agent.name;
  const [variant, setVariant] = useState("launchpad");
  const [fullscreen, setFullscreen] = useState(false);
  const shell = useMemo(() => ({ name, fullscreen, setFullscreen }), [name, fullscreen]);
  return <ChatShellContext.Provider value={shell}><ChatWorkspaceContext.Provider value={{ controller, name, variant, setVariant, fullscreen, setFullscreen, clientName: activeClient?.clientName, readOnly: Boolean(readOnly), readOnlyMessage }}>{children}</ChatWorkspaceContext.Provider></ChatShellContext.Provider>;
}

// The shell owns running sessions. Navigating to another page does not unmount streams;
// switching workspace or identity always does, before any previous data can be rendered.
export function ChatWorkspaceProvider({ children }) {
  const { activeClient, member } = useApp();
  const scopeKey = JSON.stringify([member?.id || member?.email || "signed-out", activeClient?.clientSlug || "no-workspace"]);
  return <ScopedWorkspace key={scopeKey} scopeKey={scopeKey}>{children}</ScopedWorkspace>;
}

export function ChatWorkspaceLauncher() {
  const { controller } = useChat();
  const { pathname } = useLocation();
  if (pathname === "/agent/chat" || !controller.agent.available) return null;
  const running = controller.sessions.filter((session) => session.status === "running").length;
  return <Link to="/agent/chat" aria-label={`Open Chat${running ? `, ${running} sessions running` : ""}`} data-testid="chat-workspace-launcher" className="fixed bottom-5 right-5 z-40 inline-flex h-12 items-center gap-2 rounded-full bg-sm-primary px-5 text-sm font-semibold text-white shadow-lg hover:bg-sm-ink"><MessageSquare size={18} /> Chat {running > 0 ? <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs">{running}</span> : null}</Link>;
}
